import { useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { api, ApiError, errorMessage } from '../../shared/api/client';
import { Alert, Button, Field, PasswordField } from '../../shared/ui/components';
import { useSession } from '../session/SessionProvider';
import { AuthLayout } from './AuthLayout';
import { ChangeEmail } from './ChangeEmail';
import { GOOGLE_ERRORS, GoogleButton } from './GoogleButton';

/** Solo se permite volver a rutas internas de /app o a la invitación /unirse (evita redirecciones abiertas). */
export function safeReturnPath(value: string | null): string {
  return value && (value.startsWith('/app') || value.startsWith('/unirse/')) && !value.startsWith('//') ? value : '/app';
}

const SAVED_EMAIL_KEY = 'keora.correo';

function savedEmail(): string {
  try {
    return localStorage.getItem(SAVED_EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
}

/** «Recordar mi correo»: se guarda solo en este navegador; la contraseña la recuerda el propio navegador. */
function rememberEmail(email: string | null): void {
  try {
    if (email) localStorage.setItem(SAVED_EMAIL_KEY, email);
    else localStorage.removeItem(SAVED_EMAIL_KEY);
  } catch {
    // Sin almacenamiento: no pasa nada.
  }
}

export function LoginPage() {
  const { state, login } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(() => GOOGLE_ERRORS[params.get('google') ?? ''] ?? null);
  const [busy, setBusy] = useState(false);
  const keepRef = useRef<HTMLInputElement>(null);
  const [unverified, setUnverified] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const target = safeReturnPath(params.get('volver'));
  const [initialEmail] = useState(savedEmail);

  if (state.status === 'authenticated') return <Navigate to={target} replace />;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    setUnverified(null);
    setResent(false);
    try {
      const email = String(form.get('email')).trim();
      await login(email, String(form.get('password')), form.get('keep') === 'on');
      rememberEmail(form.get('rememberEmail') === 'on' ? email : null);
      navigate(target, { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.details?.reason === 'email_not_verified') setUnverified(String(form.get('email')).trim());
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Iniciar sesión"
      footer={
        <>
          ¿No tienes cuenta? <Link to="/registro">Crear cuenta</Link>
        </>
      }
    >
      <GoogleButton label="Entrar con Google" volver={target} remember={() => keepRef.current?.checked ?? true} />
      <form className="form" onSubmit={onSubmit} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        {unverified ? (
          <Button
            type="button"
            variant="secondary"
            disabled={resent}
            onClick={() => {
              setResent(true);
              void api.post('/identity/email-verifications/resend', { email: unverified }).catch(() => undefined);
            }}
          >
            {resent ? `Te enviamos otro enlace a ${unverified}` : 'Enviarme el enlace otra vez'}
          </Button>
        ) : null}
        <Field label="Correo" name="email" type="email" autoComplete="username" required defaultValue={initialEmail} autoFocus={!initialEmail} />
        <PasswordField label="Contraseña" name="password" autoComplete="current-password" required autoFocus={Boolean(initialEmail)} />
        <div className="login-options">
          <label className="check">
            <input type="checkbox" name="rememberEmail" defaultChecked /> Recordar mi correo
          </label>
          <label className="check">
            <input ref={keepRef} type="checkbox" name="keep" defaultChecked /> Mantener la sesión iniciada
          </label>
        </div>
        <p className="small">
          <Link to="/olvide-contrasena">¿Olvidaste tu contraseña?</Link> · <Link to="/hijo">Soy menor: entrar con permiso de mis papás</Link>
        </p>
        <Button type="submit" busy={busy} className="btn--block">
          Entrar
        </Button>
      </form>
      {unverified ? (
        <ChangeEmail
          email={unverified}
          onChanged={(next) => {
            setUnverified(next);
            setError(null);
            setResent(true);
          }}
        />
      ) : null}
    </AuthLayout>
  );
}
